import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Spin } from "antd";
import { fetchPackageById } from "../api/subscriptionPackagesApi";
import { createSubscription } from "../api/subscriptionsApi";
import { featureTexts, formatMoney, periodLabel, typeLabel, cycleLabel } from "../utils/pricing";
import { toastError } from "../utils/toast";
import PlansExtras from "../components/PlansExtras";
import "./css/pricing.css";

const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
};

export default function SubscribeDetailsPage() {
  const [searchParams] = useSearchParams();
  const packageId = searchParams.get("package_id");
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState(EMPTY_FORM);

  useEffect(() => {
    let on = true;
    (async () => {
      if (!packageId) {
        setLoading(false);
        return;
      }
      try {
        const data = await fetchPackageById(packageId);
        if (on) setPkg(data);
      } catch {
        toastError("Unable to load the selected package.");
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => {
      on = false;
    };
  }, [packageId]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const first_name = values.first_name.trim();
    const last_name = values.last_name.trim();
    const email = values.email.trim();
    const phone = values.phone.trim();

    if (first_name.length < 2) {
      toastError("Please enter your first name.");
      return;
    }
    if (last_name.length < 2) {
      toastError("Please enter your last name.");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      toastError("Please enter a valid email.");
      return;
    }
    if (phone.replace(/\D/g, "").length < 7) {
      toastError("Please enter a valid phone number.");
      return;
    }

    setSaving(true);
    try {
      const subscription = await createSubscription({
        package_id: Number(packageId),
        first_name,
        last_name,
        email,
        phone,
      });
      navigate(`/subscribe/payment/${subscription.public_token}`, { replace: true });
    } catch (err) {
      toastError(err?.response?.data?.message || "Failed to save your details.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cl-pricing-loading">
        <Spin />
      </div>
    );
  }

  if (!pkg) {
    return (
      <section className="cl-checkout">
        <div className="cl-container">
          <h1>Package not found</h1>
          <p>The selected package is unavailable. Please choose another plan.</p>
        </div>
      </section>
    );
  }

  const extrasType = String(pkg.subscription_type || "personal").toLowerCase() === "business"
    ? "business"
    : "personal";

  return (
    <>
    <section className="cl-checkout">
      <div className="cl-container">
        <p className="cl-eyebrow">Plan and business details</p>
        <h1>Review your plan and enter your details</h1>
        <div className="cl-checkout-grid">
          <aside className="cl-summary-card">
            <div className="cl-summary-head">
              <span className="cl-summary-icon">☰</span>
              <div>
                <h2>Plan summary</h2>
                <p>Review the selected plan, included support and final total.</p>
              </div>
            </div>
            <div className="cl-summary-plan">
              <div>
                <small>Selected plan</small>
                <strong>{pkg.name}</strong>
              </div>
              <div className="cl-summary-price">
                {formatMoney(pkg.price, pkg.currency)}
                <span>{periodLabel(pkg)}</span>
              </div>
            </div>
            <ul className="cl-plan-features">
              {featureTexts(pkg).map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
            <p className="cl-summary-meta">
              {typeLabel(pkg.subscription_type)} · {cycleLabel(pkg.billing_cycle)} ·{" "}
              {pkg.duration_value} {pkg.duration_unit}
            </p>
          </aside>

          <form className="cl-checkout-card" onSubmit={onSubmit}>
            <div className="cl-summary-head">
              <span className="cl-summary-icon">☺</span>
              <div>
                <h2>Your details</h2>
                <p>Enter the contact details we should use for the account and follow-up.</p>
              </div>
            </div>
            <div className="cl-form-grid">
              <label>
                First name
                <input name="first_name" value={values.first_name} onChange={onChange} required />
              </label>
              <label>
                Last name
                <input name="last_name" value={values.last_name} onChange={onChange} required />
              </label>
              <label>
                Email
                <input type="email" name="email" value={values.email} onChange={onChange} required />
              </label>
              <label>
                Phone
                <input name="phone" value={values.phone} onChange={onChange} required />
              </label>
            </div>
            <button type="submit" className="btn btn-solid cl-checkout-submit" disabled={saving}>
              {saving ? "Saving..." : "Continue to payment"}
            </button>
          </form>
        </div>
      </div>
    </section>
    <PlansExtras type={extrasType} />
    </>
  );
}
